export type AllyJobKind = 'CLICK_INS' | 'PAVE' | 'E_CARS';

export interface JobKindSource {
  year?: string | number | null;
  make?: string | null;
  model?: string | null;
  description?: string | null;
  allyInspectionProvider?: string | null;
  clickInsInspectionCaseId?: string | null;
  lineOfBusiness?: string | null;
  category?: {
    name?: string | null;
    isAutomotive?: boolean;
    lineOfBusiness?: string | null;
    type?: string | null;
  } | null;
  subCategory?: { name?: string | null } | null;
}

const lineOfBusiness = (job: JobKindSource): string =>
  job.category?.lineOfBusiness ??
  job.category?.type ??
  job.lineOfBusiness ??
  '';

export const isAutomotiveJob = (job: JobKindSource): boolean =>
  job.category?.isAutomotive === true &&
  lineOfBusiness(job) !== 'ASSET_VERIFICATION';

export const allyJobKind = (job: JobKindSource): AllyJobKind | null => {
  if (
    job.allyInspectionProvider === 'CLICK_INS' ||
    Boolean(job.clickInsInspectionCaseId)
  ) {
    return 'CLICK_INS';
  }
  if (
    job.allyInspectionProvider === 'PAVE' ||
    lineOfBusiness(job) === 'AMAZON_PAVE'
  ) {
    return 'PAVE';
  }
  if (lineOfBusiness(job) === 'E_CARS') {
    return 'E_CARS';
  }
  return null;
};

export const isNonAutomotiveJob = (job: JobKindSource): boolean =>
  allyJobKind(job) == null &&
  (job.category?.isAutomotive === false ||
    lineOfBusiness(job) === 'ASSET_VERIFICATION');

export type JobKindBucket = 'AUTOMOTIVE' | 'NON_AUTOMOTIVE' | AllyJobKind;

export const jobKindBuckets: JobKindBucket[] = [
  'AUTOMOTIVE',
  'NON_AUTOMOTIVE',
  'E_CARS',
  'PAVE',
  'CLICK_INS',
];

export const jobKindBucket = (job: JobKindSource): JobKindBucket => {
  const ally = allyJobKind(job);
  if (ally != null) {
    return ally;
  }
  return isAutomotiveJob(job) ? 'AUTOMOTIVE' : 'NON_AUTOMOTIVE';
};

export const countJobsByKind = (
  jobs: JobKindSource[]
): Record<JobKindBucket, number> => {
  const counts: Record<JobKindBucket, number> = {
    AUTOMOTIVE: 0,
    NON_AUTOMOTIVE: 0,
    E_CARS: 0,
    PAVE: 0,
    CLICK_INS: 0,
  };
  jobs.forEach((job) => {
    counts[jobKindBucket(job)] += 1;
  });
  return counts;
};

export const jobKindTitleKey = (bucket: JobKindBucket): string => {
  switch (bucket) {
    case 'AUTOMOTIVE':
      return 'jobs.kindAutomotive';
    case 'NON_AUTOMOTIVE':
      return 'jobs.kindNonAutomotive';
    case 'E_CARS':
      return 'jobs.kindECars';
    case 'PAVE':
      return 'jobs.kindPave';
    case 'CLICK_INS':
      return 'jobs.kindClickIns';
    default: {
      const unreachable: never = bucket;
      return unreachable;
    }
  }
};

export const jobKindHintKey = (bucket: JobKindBucket): string => {
  switch (bucket) {
    case 'AUTOMOTIVE':
      return 'jobs.kindAutomotiveHint';
    case 'NON_AUTOMOTIVE':
      return 'jobs.kindNonAutomotiveHint';
    case 'E_CARS':
      return 'jobs.kindECarsHint';
    case 'PAVE':
      return 'jobs.kindPaveHint';
    case 'CLICK_INS':
      return 'jobs.kindClickInsHint';
    default: {
      const unreachable: never = bucket;
      return unreachable;
    }
  }
};

export const jobKindDetailKey = (job: JobKindSource): string => {
  const bucket = jobKindBucket(job);
  switch (bucket) {
    case 'AUTOMOTIVE':
      return 'jobs.kindDetailAutomotive';
    case 'NON_AUTOMOTIVE':
      return 'jobs.kindDetailNonAutomotive';
    case 'E_CARS':
      return 'jobs.kindDetailECars';
    case 'PAVE':
      return 'jobs.kindDetailPave';
    case 'CLICK_INS':
      return 'jobs.kindDetailClickIns';
    default: {
      const unreachable: never = bucket;
      return unreachable;
    }
  }
};

export const jobListTitle = (job: JobKindSource, fallback: string): string => {
  if (isAutomotiveJob(job)) {
    return (
      [job.year, job.make, job.model].filter(Boolean).join(' ') || fallback
    );
  }
  return (
    job.description || job.subCategory?.name || job.category?.name || fallback
  );
};
